// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import type { FC, ReactNode } from "react";

import {
  Column,
  Combobox,
  NumberInput,
  Row,
  Text,
  TextInput,
} from "@hightouch/htui";

import type { PropertyCondition } from "src/types/visual";

import { ErrorMessage } from "./error-message";
import type { UpdaterEnforceDefined } from "src/utils/immutable-helpers";

type Props = {
  condition: PropertyCondition;
  isDisabled?: boolean;
  error?: ReactNode;
  suggestionsLoading?: boolean;
  suggestionOptions;
  suggestionsTip: string;
  bigint?: boolean;
  onUpdate: (updater: UpdaterEnforceDefined<PropertyCondition>) => void;
};

export const NumberRangeInput: FC<Readonly<Props>> = ({
  condition,
  isDisabled = false,
  error,
  suggestionsLoading,
  suggestionOptions,
  suggestionsTip,
  onUpdate,
  bigint = false,
}) => {
  const wrappedOnChange = (
    key: "min" | "max",
    value: string | number | undefined,
  ) => {
    const numericValue = Number(value);

    // Ignore strings that can't be casted to numbers
    if (typeof value === "string" && isNaN(numericValue)) {
      return;
    }

    // Allow undefined so that the input can be reset (e.g., the user deletes the existing value)
    onUpdate((condition) => {
      // Ensure condition.value is an object before setting properties
      if (condition.value == null) {
        condition.value = {};
      }
      condition.value[key] =
        value == undefined ? value : bigint ? value : numericValue;
    });
  };

  const updateMinValue = (value: string | number | undefined) =>
    wrappedOnChange("min", value);
  const updateMaxValue = (value: string | number | undefined) =>
    wrappedOnChange("max", value);

  const minValue = condition.value?.min;
  const maxValue = condition.value?.max;

  const comboboxProps = {
    isClearable: true,
    isDisabled,
    isLoading: suggestionsLoading,
    options: suggestionOptions ?? [],
    tip: suggestionsTip,
  };

  return (
    <Column>
      <Row gap={2}>
        {suggestionOptions.length ? (
          <Combobox
            {...comboboxProps}
            placeholder="search min…"
            value={minValue}
            onChange={(option) => updateMinValue(option)}
            onCreateOption={(value) => updateMinValue(value)}
          />
        ) : bigint ? (
          <TextInput
            isDisabled={isDisabled}
            isInvalid={Boolean(error)}
            placeholder="min"
            value={minValue ?? ""}
            width="auto"
            onChange={(e) => updateMinValue(e.target.value)}
            type="integer"
          />
        ) : (
          <NumberInput
            isDisabled={isDisabled}
            isInvalid={Boolean(error)}
            placeholder="min"
            value={minValue ?? Number.NaN}
            width="auto"
            onChange={updateMinValue}
          />
        )}
        <Column justifyContent="center">
          <Text>and</Text>
        </Column>
        {suggestionOptions.length ? (
          <Combobox
            {...comboboxProps}
            placeholder="search max…"
            value={maxValue}
            onChange={(option) => updateMaxValue(option)}
            onCreateOption={(value) => updateMaxValue(value)}
          />
        ) : bigint ? (
          <TextInput
            isDisabled={isDisabled}
            isInvalid={Boolean(error)}
            placeholder="max"
            value={maxValue ?? ""}
            width="auto"
            onChange={(e) => updateMaxValue(e.target.value)}
            type="integer"
          />
        ) : (
          <NumberInput
            isDisabled={isDisabled}
            isInvalid={Boolean(error)}
            placeholder="max"
            value={maxValue ?? Number.NaN}
            width="auto"
            onChange={updateMaxValue}
          />
        )}
      </Row>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Column>
  );
};
